/* eslint-disable max-classes-per-file */
import { IListValidationMessage } from "~app/common/components/list/div-list/div-list.models";
import { Anskaffningsformer, Planeringsstatus } from "~app/common/models/constants";
import { ArbetsorderTidpunktDTO } from "~app/common/models/planera/ArbetsorderTidpunktDTO";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IArbetsorder } from "~app/features/planera/model/IArbetsorder";
import { IMaskinlag } from "~app/features/planera/model/IMaskinLag";
import { IOperationBase } from "~app/features/planera/model/IOperationBase";
import { Produktionsstatusar } from "../../services/arbetsorder.service";

export class MasshanteringArbetsorderViewModel {
    masshanteringArbetsorder: MasshanteringArbetsorder[];

    constructor(arbetsorderInfo: IArbetsorder[]) {
        this.masshanteringArbetsorder = [];
        arbetsorderInfo.forEach(x => this.masshanteringArbetsorder.push(new MasshanteringArbetsorder(x)));
    }

    get isOnlyGrotskotning(): boolean {
        return this.masshanteringArbetsorder.every(z => z.isGrotskotning);
    }

    get isMixedMode(): boolean {
        if (this.masshanteringArbetsorder.length <= 1) {
            return false;
        }

        const comp = this.masshanteringArbetsorder[0].isGrotskotning;
        return this.masshanteringArbetsorder.some(z => z.isGrotskotning !== comp);
    }
}

export class MasshanteringArbetsorder {
    id: number;
    namn: string;
    beteckning: string;
    anskaffningsform: string;
    planeratMaskinlagId?: number;
    nyttMaskinlagId?: number;

    isGrotskotning: boolean;
    isLeveransvirke: boolean;
    isVirkeskop: boolean;
    avverkningsgruppForkortning: string;
    planeringsstatus: string;
    produktionsstatus: string;
    tidpunkter: ArbetsorderTidpunktDTO;
    operationer: IOperationBase[];
    periodsattPeriod?: string;
    affarsform: string;
    internProduktionsstatus: number;

    suggestedStart?: string;
    maskinlag: IMaskinlag[];
    intent: IntentType;
    ref: IArbetsorder;

    static readonly applicableAnskffningsformer = [
        Anskaffningsformer.Egenskog,
        Anskaffningsformer.Virkeskop,
        Anskaffningsformer.CentralaVirkeskop
    ];

    get isMaskinlagApplicable(): boolean {
        return !this.isLeveransvirke;
    }

    get isProduktionsstatusEjPaborjad(): boolean {
        return this.internProduktionsstatus === Produktionsstatusar.produktionEjPaborjad;
    }

    get isPlaneringsstatusGreaterThanUnderPlanering(): boolean {
        return this.planeringsstatus === Planeringsstatus.Tillgangligt || this.planeringsstatus === Planeringsstatus.Fardigplanerat;
    }

    get validationMessages(): IListValidationMessage[] {
        if(!MasshanteringArbetsorder.applicableAnskffningsformer.some(x => x === this.anskaffningsform)) {
            return [
                { type: "warning",
                    message: "Masshantering är endast tillgängligt för egenskog och virkesköp" }
            ];
        }

        if (this.isLeveransvirke) {
            return [
                { type: "warning",
                    message: "Masshantering är inte aktuellt för Leveransvirke" }
            ];
        }

        const messages = [];
        if (!this.isProduktionsstatusEjPaborjad) {
            messages.push({ type: "warning",
                message: "Går ej att planera/avplanera då produktion är påbörjad" });
        }

        if (!this.isPlaneringsstatusGreaterThanUnderPlanering) {
            messages.push({ type: "warning",
                message: "Arbetsorder måste ha planeringsstatus > Under planering" });
        }

        if (this.isGrotskotning) {
            messages.push({ type: "warning",
                message: "Grot arbetsorder kan inte använda produktionsmall" });
        }

        return messages;
    }

    get isLocked(): boolean {
        return this.isLeveransvirke ||
         !this.isProduktionsstatusEjPaborjad ||
         !this.isPlaneringsstatusGreaterThanUnderPlanering ||
         !MasshanteringArbetsorder.applicableAnskffningsformer.some(x => x === this.anskaffningsform);
    }

    get applicable(): boolean {
        if (this.isLocked) {
            return false;
        }

        switch (this.intent) {
            case IntentType.schemalagg:
            case IntentType.setMaskinlag:
                return ObjectUtils.hasValue(this.nyttMaskinlagId);
            default:
                return true;
        }
    }

    constructor(arbetsorderInfo: IArbetsorder) {
        this.id = arbetsorderInfo.id;
        this.namn = arbetsorderInfo.namn;
        this.beteckning = arbetsorderInfo.beteckning;
        this.anskaffningsform = arbetsorderInfo.anskaffningsform;
        this.planeratMaskinlagId = arbetsorderInfo.maskinlagId;
        this.isGrotskotning = arbetsorderInfo.isGrotskotning;
        this.isLeveransvirke = arbetsorderInfo.isLeveransvirke;
        this.isVirkeskop = arbetsorderInfo.isVirkeskop;
        this.avverkningsgruppForkortning = arbetsorderInfo.avverkningsgruppForkortning;
        this.tidpunkter = arbetsorderInfo.tidpunkter;
        this.planeringsstatus = arbetsorderInfo.planeringsstatus;
        this.produktionsstatus = arbetsorderInfo.produktionsstatus;
        this.operationer = arbetsorderInfo.operationer;
        this.periodsattPeriod = arbetsorderInfo.periodsattPeriod;
        this.affarsform = arbetsorderInfo.affarsform;
        this.internProduktionsstatus = arbetsorderInfo.internProduktionsstatus;
        this.ref = arbetsorderInfo;
    }
}

export interface IIntentTypeItem {
    key: IntentType;
    value: string;
}

export enum IntentType {
    periodsatt = 0,
    schemalagg = 1,
    avplanera = 2,
    setMaskinlag = 3
}
